import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ReasonMap } from '@shared/models/reasonMap.model';
import { getBrightness } from '@shared/utils/cross-functions';

@Component({
  selector: 'app-code-rh-badge',
  templateUrl: './code-rh-badge.component.html',
  styleUrls: ['./code-rh-badge.component.scss'],
  standalone: false,
})
export class CodeRHBadgeComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('textContainer', { static: false }) public textContainer!: ElementRef;

  @Input() public text: string = 'Action';

  @Input() public isIcon: boolean = true;

  @Input() public icon: string = 'ellipse';

  @Input() public color: string = '#FFFFFF';

  // iconType : 'ion-icon' | 'svg-icon'
  @Input() public iconType: string = 'ion-icon';

  @Input() public isConsult: string = '';

  @Input() public oneCalendar = false;

  @Input() public showCircle: boolean = true;

  @Input() public service: string = '';

  @Input() public reasonMap: ReasonMap = {};

  @Input() public startTime: Date | string = '';

  @Input() public endTime: Date | string = '';

  @Input() public textSize: string = '10';

  // If true a plus icon appears above the badge
  @Input() public isExtraHours: boolean = false;

  @Input() public withClose: boolean = false;

  @Input() public squareShaped: boolean = false;

  @Input() public long: boolean = false;

  @Output() public remove = new EventEmitter();

  public rhCodeColor: string = '#FFFFFF';

  public displayTextValue: string = '';

  public displayIcon: string = 'ellipse';

  public displayIconType: string = 'mat-icon';

  public hasOverflow: boolean = false;

  private resizeObserver!: ResizeObserver;

  private mutationObserver!: MutationObserver;

  ngOnInit(): void {
    if (this.color) {
      this.initRhCodeColor();
      if (this.startTime && (this.startTime as string).length === 5) {
        this.startTime = this.createDateFromTime(this.startTime as string);
      }
      if (this.endTime && (this.endTime as string).length === 5) {
        this.endTime = this.createDateFromTime(this.endTime as string);
      }
    } else {
      this.rhCodeColor = '#000000';
    }
    if (Object.keys(this.reasonMap).length !== 0) {
      this.isIcon = this.reasonMap.icon !== null;
    }
    this.displayTextValue = this.displayText();
    this.getIcon();
    this.getIconType();
  }

  ngAfterViewInit(): void {
    const el = this.textContainer?.nativeElement;
    if (!el) {
      return;
    }

    // ResizeObserver: watches size/layout changes
    this.resizeObserver = new ResizeObserver(() => {
      this.checkOverflow(el);
    });
    this.resizeObserver.observe(el);

    // MutationObserver: watches for dynamic content changes
    this.mutationObserver = new MutationObserver(() => {
      this.checkOverflow(el);
    });
    this.mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // Initial overflow check after rendering
    setTimeout(() => this.checkOverflow(el), 0);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private checkOverflow(el: HTMLElement): void {
    this.hasOverflow = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
  }

  onTriggerRemove() {
    if (this.withClose) {
      this.remove.emit();
    }
  }

  displayText() {
    if (this.text) {
      if (this.text === 'SMARTOP_DAY_ASTREINTE' || this.text === 'SMARTOP_NIGHT_ASTREINTE') {
        this.text = 'Astreinte';
      }
    }
    if (this.startTime && this.endTime) {
      let start = null;
      let end = null;
      if ((this.startTime as string).length === 5 && (this.endTime as string).length === 5) {
        start = this.startTime;
        end = this.endTime;
      } else {
        start = new Date(this.startTime).toISOString().substring(11, 16);
        end = new Date(this.endTime).toISOString().substring(11, 16);
      }
      if (!this.long) {
        return `${this.text} | ${start} - ${end}`;
      } else {
        return `${start}\n${end}`;
      }
    }
    return this.text;
  }

  getIcon() {
    if (this.reasonMap && this.reasonMap.icon) {
      this.showCircle = false;
      this.displayIcon = `assets/icons/reasons-maps/${this.reasonMap.icon}.svg`;
      return;
    }
    this.displayIcon = 'ellipse';
  }

  getIconType() {
    if (this.reasonMap && this.reasonMap.icon) {
      this.displayIconType = 'svg-icon';
      return;
    }
    this.displayIconType = 'mat-icon';
  }

  initRhCodeColor(): void {
    if (this.color && getBrightness(this.color) < 128) {
      this.rhCodeColor = '#FFFFFF';
    } else {
      this.rhCodeColor = '#000000';
    }
  }

  createDateFromTime(timeString: string, baseDate: any = new Date()) {
    const [hours, minutes] = timeString.split(':').map(Number);

    const year = baseDate.getUTCFullYear();
    const month = baseDate.getUTCMonth();
    const day = baseDate.getUTCDate();

    const combinedDate = new Date(Date.UTC(year, month, day, hours, minutes, 0, 0));

    return combinedDate;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showCircle']) {
      this.showCircle = changes['showCircle'].currentValue;
    }
    if (changes['color'] && this.color) {
      this.initRhCodeColor();
    }
    if (changes['startTime'] || changes['endTime']) {
      if (this.startTime && (this.startTime as string).length === 5) {
        this.startTime = this.createDateFromTime(this.startTime as string);
      }
      if (this.endTime && (this.endTime as string).length === 5) {
        this.endTime = this.createDateFromTime(this.endTime as string);
      }
      this.displayTextValue = this.displayText();
    }
    if (changes['reasonMap']) {
      this.getIcon();
      this.getIconType();
    }

    setTimeout(() => {
      if (this.textContainer?.nativeElement) {
        this.checkOverflow(this.textContainer.nativeElement);
      }
    }, 0);
    if (changes['text']) {
      this.displayTextValue = this.displayText();
    }
  }
}
